import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import type { RegistrySummary } from '../../api/areas/registry';
import { Badge } from '../../components/Badge';
import { Section, StatCard, StatGrid } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AdapterLabel, Capabilities, EnumBadge, enumLabel, HEALTH, HealthTiles, REGISTRY_STATUS, RefreshButton, TldRef, useRegistryCrumbs } from './shared';

/** One registry, opening its own page; the queue counters link to the queue they count. */
function RegistryCard({ r }: { r: RegistrySummary }) {
  const { t } = useTranslation('registry');
  const to = `/registry/registries/${encodeURIComponent(r.code)}`;
  const attention = HEALTH.some((h) => h.count(r) > 0);
  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Link to={to} className="text-primary hover:underline">
            {r.name}
          </Link>
          <EnumBadge group="registryStatus" map={REGISTRY_STATUS} value={r.status} />
          {attention ? <Badge tone="warn">{t('registries.attention')}</Badge> : <Badge tone="ok">{t('registries.healthy')}</Badge>}
        </span>
      }
      description={<code className="text-xs">{r.code}</code>}
      actions={
        <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
          {t('registries.open')} <ChevronRight className="size-3.5" aria-hidden="true" />
        </Link>
      }
    >
      <div className="space-y-5">
        <HealthTiles r={r} />
        <KeyValue
          columns={3}
          rows={[
            [t('registries.adapter'), <AdapterLabel key="a" adapter={r.adapter} />],
            [
              t('registries.tlds'),
              r.tlds.length === 0 ? (
                t('none')
              ) : (
                <span key="t" className="flex flex-wrap gap-x-3 gap-y-1">
                  {r.tlds.map((z) => (
                    <span key={z.id} className={cn(z.status !== 'ACTIVE' && 'opacity-60')}>
                      <TldRef zone={z.zone} id={z.id} sub={z.status === 'ACTIVE' ? undefined : enumLabel('registryStatus', z.status)} />
                    </span>
                  ))}
                </span>
              ),
            ],
            [
              t('registries.environments'),
              r.connection_enabled === null ? (
                <Badge key="e" tone="warn">
                  {t('registries.notConfigured')}
                </Badge>
              ) : r.connection_enabled ? (
                <Badge key="e" tone="ok">
                  {t('epp.active')}
                </Badge>
              ) : (
                <Badge key="e" tone="neutral" icon={false}>
                  {t('epp.inactive')}
                </Badge>
              ),
            ],
          ]}
        />
        <div className="border-t pt-4">
          <Capabilities c={r.capabilities} />
        </div>
      </div>
    </Section>
  );
}

/** `/registry/registries`: every registry, what it allows and what is waiting on it (spec §24–§25). */
export function RegistriesPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('registries.title'));
  const crumbs = useRegistryCrumbs();
  const result = useApi((signal) => api.listRegistries({ signal }), []);
  const items = result.status === 'success' ? result.data.items : [];
  return (
    <>
      <PageHeader title={t('registries.title')} crumbs={crumbs} subtitle={t('registries.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      {result.status === 'loading' ? <Loading rows={3} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('registries.loadError')} /> : null}
      {result.status === 'success' && items.length === 0 ? <EmptyState title={t('registries.empty')} /> : null}
      {items.length > 0 ? (
        <div className="space-y-6">
          <section aria-label={t('registries.totals')}>
            <StatGrid>
              {HEALTH.map((h) => {
                const n = items.reduce((sum, r) => sum + h.count(r), 0);
                return (
                  <StatCard
                    key={h.key}
                    label={t(`registries.${h.key}`)}
                    icon={h.icon}
                    value={formatCount(n)}
                    tone={n > 0 ? h.tone : 'ok'}
                    hint={t(`registries.${h.key}Hint`)}
                    to={h.to('')}
                    linkLabel={t('registries.viewList')}
                  />
                );
              })}
            </StatGrid>
          </section>
          {items.map((r) => (
            <RegistryCard key={r.id} r={r} />
          ))}
        </div>
      ) : null}
    </>
  );
}
